import React, { FC } from 'react';
import DomPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../hooks/redux';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { authorizationApi } from '../../../../services/apiServices/user/authorization';
import { authSlice } from '../../../../store/reducers/AuthSlice';
import { userInfoSlice } from '../../../../store/reducers/UserInfoSlice';
import { ButtonClassNames, LocalStorageItems } from '../../../../models/Common';
import { ApiError } from '../../../../models/ApiError';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import { AnimatedIcon } from '../../AnimatedIcon';
import WarningIconLight from '../../../../assets/images/lottieIcons/warning/warning.light.json';
import WarningIconDark from '../../../../assets/images/lottieIcons/warning/warning.dark.json';
import { getErrorMessage } from '../../../../utils';

interface ITopUpWarning {
  userId: number | string | undefined;
  handleClose: () => void;
}

export const TopUpWarning: FC<ITopUpWarning> = ({ userId, handleClose }) => {
  const { t } = useTranslation();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { logout } = authSlice.actions;
  const [logoutApi] = authorizationApi.useLogoutMutation();
  const { resetUserInfoAfterLogout } = userInfoSlice.actions;
  const sanitizer = DomPurify.sanitize;
  const dispatch = useAppDispatch();

  const onLogout = async () => {
    try {
      await logoutApi('')
        .unwrap()
        .then(() => {
          localStorage.removeItem(LocalStorageItems.ControlledAccount);
          dispatch(resetUserInfoAfterLogout());
          dispatch(logout());
        });
    } catch (err) {
      const error = err as ApiError;
      if (error.status !== 403) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const logoutHandler = () => {
    onLogout();
    handleClose();
  };

  return (
    <div className="modals">
      <div className="modals__header modals__header__top-up">
        <AnimatedIcon lightIcon={WarningIconLight} darkIcon={WarningIconDark} className="icon-warning" />
        <article className="modals__header__title">{t('common.attention')}</article>
        <p className="modals__header__description">{t('topUpBalance.anotherContractTopUp')}</p>
        <p
          className="modals__header__description"
          dangerouslySetInnerHTML={{
            __html: sanitizer(t('topUpBalance.personalAccountEnter', { val: userId })),
          }}
        />
      </div>
      <div className="modals__btn-group">
        <CustomMUIButton
          label={t('header.toLogOut')}
          handleClick={logoutHandler}
          className={ButtonClassNames.PrimaryFulfilled}
          isBold
        />
      </div>
    </div>
  );
};
